import clsx from "clsx";
import { ReactNode } from "react";
import { Link } from "react-router";

import { KEYBOARD_TAB_INDEX } from "@/utils/keyboardTabIndex";

interface Props {
  icon: ReactNode;
  label: string;
  active?: boolean;
  target?: string;
  href?: string;
  onClick?: () => void;
  ariaCurrent?: "page";
  /**
   * Regular weight — used by expanded DR/legal links.
   * Default is SemiBold.
   */
  regular?: boolean;
  /** Tighter spacing used by the legal-links dropdown, vs the default nav row. */
  compact?: boolean;
}

const rowClassName = (compact?: boolean) =>
  clsx(
    // relative + after: draw the Figma focus ring (2px, offset 4px outside
    // the row, 8px corner radius) — a plain `outline`/`ring` can't have a
    // radius independent of the row's own 4px radius.
    "relative flex items-center rounded-md text-body-base leading-none text-foreground-primary outline-none hover:bg-surface-secondary active:bg-surface-secondary focus-visible:bg-surface-secondary after:pointer-events-none after:absolute after:inset-[-4px] after:rounded-lg after:border-2 after:border-transparent after:content-[''] focus-visible:after:border-border-primary",
    compact ? "gap-1 px-1 py-1" : "gap-2 px-2 py-2",
  );

/**
 * A single navigation/menu row shared by the desktop Sidebar and the
 * mobile/tablet burger drawer. Renders as a router Link, an external link,
 * or a plain button depending on which props are given.
 */
export const NavRow = ({
  icon,
  label,
  active,
  target,
  href,
  onClick,
  ariaCurrent,
  regular,
  compact,
}: Props) => {
  const className = clsx(
    rowClassName(compact),
    // !font-semibold: form.scss's unlayered `button { font-weight: normal }`
    // otherwise wins over Tailwind utilities on <button> rows (e.g. logout).
    regular ? "font-normal" : "!font-semibold",
    active && "bg-surface-tertiary",
  );

  if (target) {
    return (
      <li>
        <Link
          className={className}
          to={target}
          tabIndex={KEYBOARD_TAB_INDEX}
          onClick={onClick}
          aria-current={ariaCurrent}
        >
          {icon}
          {label}
        </Link>
      </li>
    );
  }

  if (href) {
    return (
      <li>
        <a
          className={className}
          href={href}
          tabIndex={KEYBOARD_TAB_INDEX}
          target="_blank"
          rel="noreferrer"
          onClick={onClick}
        >
          {icon}
          {label}
        </a>
      </li>
    );
  }

  return (
    <li>
      <button
        className={clsx(className, !compact && "w-full")}
        type="button"
        tabIndex={KEYBOARD_TAB_INDEX}
        onClick={onClick}
      >
        {icon}
        {label}
      </button>
    </li>
  );
};
